import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import NextLink from 'next/link';
import useAuth from '../../../hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axiosInstance from '../../../utils/axios';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { isMaster } from '../../../utils/perfil';
import { Num } from '../../../components/gps';
import FiltroRegional from '../../../components/gps/FiltroRegional';
import UsuariosOnline from './components/UsuariosOnline';
import { cn } from '../../../lib/utils';
import TituloTela from '../../../components/gps/TituloTela';

/* ============================================================================
   Home - visual novo (shadcn + Tailwind).

   Reescrita no lugar. Contrato, o mesmo de antes:
     GET geral/home[?regional=N]
     GET /users/regionais/:codigo   (para o filtro de regional)

   TUDO o que a versao MUI mostrava continua aqui:
     - o filtro de regional;
     - os tres destaques (regionais com acesso, dispositivos, usuarios ativos);
     - os cartoes de contagem (Politicas so para Master, Grupos, e para quem nao e
       perfil 0 tambem Usuarios, Dispositivos, Aplicativos e APKs), cada um
       linkando para a tela correspondente;
     - os seis indicadores do dia (logins, cadastrados, logs 15min, historicos,
       roubados, inativados) com a divisao proporcional entre eles;
     - top 5 aplicativos mais instalados (com o filtro de pacote de sistema);
     - top 5 aplicativos por consumo;
     - regionais com mais dispositivos;
     - os logins do dia com usuario, IP e horario (SO PARA MASTER desde
       29/09/2026 - e dado pessoal de terceiros; ver a guarda mais abaixo);
     - o HistoricoGeral no fim (SAIU em 29/09/2026 para Configuracoes > Logs
       do sistema, onde fica ao lado das outras fontes de registro).

   TRES MUDANCAS:

   1. O DONUT SAIU. Era um `conic-gradient` montado a mao com seis fatias e
      legenda ao lado, e serve para comparar partes de um todo - mas "logins",
      "logs" e "dispositivos cadastrados" nao sao partes de um mesmo todo, sao
      grandezas diferentes somadas por acidente. Somar log com login e depois
      mostrar a proporcao entre eles nao responde nenhuma pergunta. No lugar,
      cada indicador tem sua BARRA, com o valor e a comparacao com o maior do dia
      - que e a comparacao que existe de verdade.

   2. O `riskPercent` (roubados + inativados sobre o total) ficou EXPLICITO, com a
      conta escrita, em vez de virar so uma barra sem rotulo.

   3. Cor de acento por indicador vinha de `theme.palette` do MUI; agora sai dos
      tokens. `#64748b` (inativados) era literal e virou o cinza do tema.

   `formatCompactNumber` e o filtro de pacotes de sistema foram copiados como
   estavam - o filtro existe porque sem ele o top de instalados e so o Android.
   ============================================================================ */

interface LoginHojeDetalheItem {
  codigo: number;
  dataExecucao: string;
  mensagem: string;
  ip: string;
  usuario: { codigo: number; nome: string; email: string } | null;
}

interface AppInstaladoItem {
  nome: string;
  packageName: string;
  totalInstalacoes: number;
}

interface AppConsumoItem {
  nome: string;
  packageName: string;
  consumoDadosMoveis: number;
  consumoWifi: number;
  consumoTotal: number;
}

interface RegionalItem {
  codigo: number;
  nome: string;
  totalDispositivos: number;
}

interface InfoDashboard {
  regionalId: number;
  temTodasRegionais: boolean;
  regionaisAcesso: number[];
  apks: number;
  aplicativos: number;
  aplicativosMaisConsumo: AppConsumoItem[];
  aplicativosMaisInstalados: AppInstaladoItem[];
  dispositivos: number;
  entidades: number;
  licencas: number;
  regionaisMaisDispositivos: RegionalItem[];
  usuarios: number;
  usuariosAtivos: number;
  loginsHoje: number;
  loginsHojeDetalhes: LoginHojeDetalheItem[];
  dispositivosCadastradosHoje: number;
  logsGeradosHoje: number;
  logsUltimos15min: number;
  historicosGravadosHoje: number;
  dispositivosRoubadosHoje: number;
  dispositivosInativadosHoje: number;
}

const VAZIO: InfoDashboard = {
  regionalId: 0,
  temTodasRegionais: false,
  regionaisAcesso: [],
  apks: 0,
  aplicativos: 0,
  aplicativosMaisConsumo: [],
  aplicativosMaisInstalados: [],
  dispositivos: 0,
  entidades: 0,
  licencas: 0,
  regionaisMaisDispositivos: [],
  usuarios: 0,
  usuariosAtivos: 0,
  loginsHoje: 0,
  loginsHojeDetalhes: [],
  dispositivosCadastradosHoje: 0,
  logsGeradosHoje: 0,
  logsUltimos15min: 0,
  historicosGravadosHoje: 0,
  dispositivosRoubadosHoje: 0,
  dispositivosInativadosHoje: 0,
};

// Sem esse filtro o "top instalados" e so o proprio Android - copiado da versao
// anterior sem mudanca.
const PREFIXOS_SISTEMA = [
  'com.android.',
  'com.google.android.apps.enterprise.',
  'com.google.android.apps.work.',
  'com.google.android.googlequicksearch',
  'com.google.android.gms',
  'com.google.android.gsf',
  'com.google.android.packageinstaller',
  'android',
];

function compacto(valor: number) {
  const n = Number(valor) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.0', '')}k`;
  return String(n);
}

function bytes(valor: unknown) {
  const n = Number(valor) || 0;
  if (n <= 0) return '0 B';
  const unidades = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), unidades.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${unidades[i]}`;
}

function hora(valor?: string) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}


/* ----------------------------------------------------------------------------
   Faixa de cor no INICIO do cartao - e so ela.

   HISTORICO, porque isto mudou duas vezes hoje: primeiro os cartoes eram brancos
   com o numero colorido (a Home lia como desligada ao lado das outras telas);
   depois passaram a ter fundo e borda tingidos (virou mostrador de cores - seis
   cartoes preenchidos competindo com os proprios numeros); agora a superficie
   volta a ser neutra e a cor fica numa faixa fina no topo.

   E a versao que funciona porque separa os papeis: a faixa diz DE QUE e o cartao,
   o numero continua sendo a coisa mais forte, e nada disputa atencao com ele.

   O tom vem do mesmo `c.tom` que cada cartao ja declarava - nenhuma cor nova foi
   inventada. `overflow-hidden` impede a faixa de vazar do canto arredondado.
   ---------------------------------------------------------------------------- */
const FAIXA_CARTAO = {
  'text-info': 'bg-info',
  'text-ok': 'bg-ok',
  'text-warn': 'bg-warn',
  'text-danger': 'bg-danger',
  'text-ink': 'bg-line',
};

function faixaCartao(tom) {
  return FAIXA_CARTAO[tom || ''] || 'bg-line';
}

GeneralApp.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function GeneralApp() {
  const { user } = useAuth();
  const perfil = Number(user?.perfil ?? 0);

  const [dados, setDados] = useState<InfoDashboard>(VAZIO);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [regional, setRegional] = useState<number | null>(null);

  /* A busca de `/users/regionais/:codigo` SAIU desta tela: quem carrega a lista
     agora e o proprio `FiltroRegional`. Duas chamadas para o mesmo endpoint na
     mesma tela e uma chamada a mais em toda abertura da Home, e a lista que esta
     aqui nao era usada em nenhum outro lugar - so alimentava o `<select>`. */

  /* Trocar de regional rapido deixava a resposta ANTIGA chegar por ultimo e
     mostrar os numeros da regional A com a B selecionada. So a ultima chamada
     grava na tela. */
  const pedidoAtual = useRef(0);

  const carregar = useCallback(async () => {
    const meuPedido = ++pedidoAtual.current;
    setCarregando(true);
    setErro('');
    try {
      const param = regional ? `?regional=${regional}` : '';
      const r = await axiosInstance.get<InfoDashboard | InfoDashboard[]>(`geral/home${param}`);
      if (meuPedido !== pedidoAtual.current) return;
      // A API as vezes devolve array com um item - mesma normalizacao da versao
      // anterior.
      const bruto: any = Array.isArray(r.data) ? r.data[0] ?? {} : r.data ?? {};

      setDados({
        ...VAZIO,
        ...bruto,
        regionaisAcesso: Array.isArray(bruto.regionaisAcesso) ? bruto.regionaisAcesso : [],
        aplicativosMaisConsumo: Array.isArray(bruto.aplicativosMaisConsumo) ? bruto.aplicativosMaisConsumo : [],
        aplicativosMaisInstalados: Array.isArray(bruto.aplicativosMaisInstalados)
          ? bruto.aplicativosMaisInstalados
          : [],
        loginsHojeDetalhes: Array.isArray(bruto.loginsHojeDetalhes) ? bruto.loginsHojeDetalhes : [],
        regionaisMaisDispositivos: Array.isArray(bruto.regionaisMaisDispositivos)
          ? bruto.regionaisMaisDispositivos
          : [],
      });
      setAtualizadoEm(new Date());
    } catch (e: any) {
      if (meuPedido !== pedidoAtual.current) return;
      setErro(e?.response?.data?.message || 'Não foi possível carregar o painel.');
    } finally {
      if (meuPedido === pedidoAtual.current) setCarregando(false);
    }
  }, [regional]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Cartoes de contagem, com as mesmas regras de perfil da versao anterior.
  const cartoes = useMemo(() => {
    const lista: Array<{ titulo: string; valor: number; url: string; tom: string }> = [];

    if (isMaster(user)) {
      lista.push({
        titulo: 'Políticas',
        valor: dados.licencas,
        url: PATH_DASHBOARD.general.politicasNovo,
        tom: 'text-info',
      });
    }

    lista.push({
      titulo: 'Grupos',
      valor: dados.entidades,
      url: PATH_DASHBOARD.general.entidadesNovo,
      tom: 'text-ok',
    });

    if (perfil !== 0) {
      lista.push(
        { titulo: 'Usuários', valor: dados.usuariosAtivos, url: PATH_DASHBOARD.general.usersNovo, tom: 'text-info' },
        {
          titulo: 'Dispositivos',
          valor: dados.dispositivos,
          url: PATH_DASHBOARD.general.dispositivosNovo,
          tom: 'text-ink',
        },
        {
          titulo: 'Aplicativos',
          valor: dados.aplicativos,
          url: PATH_DASHBOARD.general.aplicativosNovo,
          tom: 'text-ok',
        },
        { titulo: 'APKs', valor: dados.apks, url: PATH_DASHBOARD.general.apksNovo, tom: 'text-warn' }
      );
    }

    return lista;
  }, [dados, perfil, user]);

  // Os seis indicadores do dia.
  const doDia = useMemo(
    () => [
      {
        titulo: 'Logins hoje',
        valor: dados.loginsHoje,
        cor: 'bg-primary',
        texto: 'text-ink',
        descricao: 'Acessos registrados no dia.',
      },
      {
        titulo: 'Dispositivos cadastrados',
        valor: dados.dispositivosCadastradosHoje,
        cor: 'bg-info',
        texto: 'text-info',
        descricao: 'Novos dispositivos de hoje.',
      },
      {
        titulo: 'Logs (últimos 15 min)',
        valor: dados.logsUltimos15min,
        cor: 'bg-ok',
        texto: 'text-ok',
        descricao: 'Logs gerados nos últimos 15 minutos.',
      },
      {
        titulo: 'Históricos gravados',
        valor: dados.historicosGravadosHoje,
        cor: 'bg-warn',
        texto: 'text-warn',
        descricao: 'Eventos persistidos hoje.',
      },
      {
        titulo: 'Roubados hoje',
        valor: dados.dispositivosRoubadosHoje,
        cor: 'bg-danger',
        texto: 'text-danger',
        alerta: true,
        descricao: 'Perda ou roubo no dia.',
      },
      {
        titulo: 'Inativados hoje',
        valor: dados.dispositivosInativadosHoje,
        cor: 'bg-warn',
        texto: 'text-warn',
        alerta: true,
        descricao: 'Equipamentos inativados hoje.',
      },
    ],
    [dados]
  );

  const maiorDoDia = Math.max(1, ...doDia.map((d) => d.valor));

  /* ALERTAS PRIMEIRO - o desenho novo pede isso, e faz sentido: num painel de
     acompanhamento, "12 roubados" precisa ser lido antes de "57 logins". Roubados e
     inativados vao para a frente SO quando sao maiores que zero; em dia limpo a
     ordem natural (logins primeiro) volta, e a lista nao fica gritando com zeros.

     `[...doDia]` porque `sort` altera o array no lugar, e `doDia` vem de um
     useMemo - ordenar direto mutaria o valor memoizado. */
  const doDiaOrdenado = useMemo(() => {
    const peso = (d: (typeof doDia)[number]) => (d.alerta && d.valor > 0 ? 0 : 1);
    return [...doDia].sort((a, b) => peso(a) - peso(b));
  }, [doDia]);

  const instalados = useMemo(
    () =>
      (dados.aplicativosMaisInstalados || [])
        .filter((i) => !PREFIXOS_SISTEMA.some((p) => String(i.packageName || '').startsWith(p)))
        .slice(0, 5),
    [dados.aplicativosMaisInstalados]
  );

  // Escala das barras da lista de instalados: o maior da propria lista. Faltava,
  // e sem ela a barra nao tinha referencia nenhuma.
  const maiorInstalado = Math.max(1, ...instalados.map((a) => Number(a.totalInstalacoes) || 0));

  const consumo = useMemo(() => (dados.aplicativosMaisConsumo || []).slice(0, 5), [dados.aplicativosMaisConsumo]);
  const maiorConsumo = Math.max(1, ...consumo.map((c) => Number(c.consumoTotal) || 0));

  const maiorRegional = Math.max(
    1,
    ...(dados.regionaisMaisDispositivos || []).map((r) => Number(r.totalDispositivos) || 0)
  );

  /* CHAMADOS ABERTOS - o unico numero do desenho novo que nao vinha de
     `geral/home` e que EXISTE em outro endpoint (`/tickets/contadores`, o mesmo que
     o rodape ja consome). Falha em silencio: o cartao desaparece se a chamada nao
     responder, porque um painel nao pode ficar em erro por causa de um cartao. */
  const [tickets, setTickets] = useState<{ abertos: number; criticos: number } | null>(null);
  useEffect(() => {
    let ativo = true;
    axiosInstance
      .get<any>('/tickets/contadores')
      .then((r) => {
        if (!ativo) return;
        const d = r.data || {};
        const abertos = Number(d.abertos ?? d.emAberto ?? d.total ?? 0);
        const criticos = Number(d.criticos ?? d.slaVencido ?? 0);
        setTickets({ abertos, criticos });
      })
      .catch(() => ativo && setTickets(null));
    return () => {
      ativo = false;
    };
  }, []);

  const emRisco = dados.dispositivosRoubadosHoje + dados.dispositivosInativadosHoje;
  const percentualRisco = dados.dispositivos
    ? Math.min(100, Math.round((emRisco / dados.dispositivos) * 100))
    : 0;

  const totalRegionais = (dados.regionaisAcesso || []).length;
  const topRegionais = (dados.regionaisMaisDispositivos || []).slice(0, 5);
  const somaTop = topRegionais.reduce((s, r) => s + (Number(r.totalDispositivos) || 0), 0);
  const fatiaTop = dados.dispositivos ? Math.round((somaTop / dados.dispositivos) * 100) : 0;

  /* PRECISA DE ATENCAO - so sinal que veio de dado real. O desenho tinha quatro
     pilulas; duas delas ("1.395 sem grupo", "14 apps parados ha 90 dias") pedem
     numeros que `geral/home` nao devolve, e inventar numero num painel de operacao
     e pior que nao mostrar. Ficam as que existem. */
  const atencao = [
    dados.dispositivosRoubadosHoje > 0 && {
      texto: `${dados.dispositivosRoubadosHoje} roubados hoje`,
      ponto: 'bg-danger',
      url: PATH_DASHBOARD.general.dispositivosNovo,
    },
    dados.dispositivosInativadosHoje > 0 && {
      texto: `${dados.dispositivosInativadosHoje} inativados hoje`,
      ponto: 'bg-warn',
      url: PATH_DASHBOARD.general.dispositivosNovo,
    },
    tickets &&
      tickets.abertos > 0 && {
        texto: `${tickets.abertos} chamado(s) aberto(s)`,
        ponto: tickets.criticos ? 'bg-danger' : 'bg-info',
        url: PATH_DASHBOARD.general.tickets,
      },
  ].filter(Boolean) as Array<{ texto: string; ponto: string; url: string }>;

  return (
    <Page title="Home">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        {/* ------------------------------------------------------- cabecalho */}
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
Situação do parque agora
              {atualizadoEm && (
                <>
                  {' · leitura das '}
                  <Num>{atualizadoEm.toLocaleTimeString('pt-BR')}</Num>
                </>
              )}
              .
                </>
              }
            >
              Olá, {user?.nome || 'você'}
            </TituloTela>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* MESMO filtro das listagens, com lupa e busca por parte do nome.
                Aqui havia um `<select>` proprio - a terceira copia do mesmo
                controle - e com 59 regionais de nomes quase todos comecando por
                "Regional" a lista nativa nao cabe na tela e a busca por primeira
                letra do select nao serve para nada.
                O componente busca a propria lista (`/users/regionais/:codigo`, o
                mesmo endpoint que esta tela ja chamava) e se esconde sozinho quando
                o usuario tem uma regional so. */}
            <FiltroRegional valor={regional} onChange={setRegional} />

            <button
              type="button"
              onClick={carregar}
              disabled={carregando}
              className="h-9 rounded-lg bg-ink px-4 text-[12px] font-bold text-surface disabled:opacity-40"
            >
              {carregando ? 'Atualizando...' : '↻ Atualizar'}
            </button>
          </div>
        </div>

        {erro && (
          <div className="mb-4 rounded-lg border border-danger bg-danger-soft p-3 text-[12.5px] text-danger">
            {erro}
            <button type="button" onClick={carregar} className="ml-2 font-semibold underline underline-offset-2">
              tentar de novo
            </button>
          </div>
        )}

        {/* --------------------------------------------- hero + cartoes ao lado */}
        <div className="mb-4 grid gap-3 lg:grid-cols-[1.15fr_1fr]">
          {/* O HERO E ESCURO de proposito: e o unico bloco que responde "qual e o
              tamanho do parque", e num painel com vinte numeros um deles precisa
              ganhar sem depender de fonte ainda maior. */}
          <div className="relative overflow-hidden rounded-lg bg-[#0f1f33] p-5 text-white shadow-card">
            <div className="flex items-start justify-between gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.09em] text-white/60">
                Base operacional
              </span>
              {dados.dispositivosCadastradosHoje > 0 && (
                <span className="text-[12px] font-bold text-[#4ade80]">
                  ↑ +<Num>{dados.dispositivosCadastradosHoje}</Num> hoje
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              <Num className="text-[40px] font-extrabold leading-none tracking-[-0.02em]">
                {dados.dispositivos.toLocaleString('pt-BR')}
              </Num>
              <span className="text-[12.5px] text-white/70">
                dispositivos em <Num>{totalRegionais}</Num> regional(is) de acesso
              </span>
            </div>

            {/* A BARRA MOSTRA CONCENTRACAO, nao quebra por situacao.
                O desenho pedia "com politica / sem grupo / inativos", e esses tres
                numeros NAO existem em `geral/home` - so o total. Em vez de inventar
                a quebra, a barra usa o que existe e responde outra pergunta real:
                quanto do parque esta nas cinco maiores regionais. */}
            {topRegionais.length > 0 && (
              <>
                <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-white/15">
                  {topRegionais.map((r, i) => (
                    <div
                      key={r.codigo ?? i}
                      title={`${r.nome}: ${Number(r.totalDispositivos).toLocaleString('pt-BR')}`}
                      style={{
                        width: `${
                          dados.dispositivos
                            ? ((Number(r.totalDispositivos) || 0) / dados.dispositivos) * 100
                            : 0
                        }%`,
                        backgroundColor: ['#4ade80', '#38bdf8', '#a78bfa', '#fbbf24', '#f472b6'][i % 5],
                      }}
                    />
                  ))}
                </div>
                <p className="mt-2 text-[12px] text-white/70">
                  As <Num>{topRegionais.length}</Num> maiores regionais concentram{' '}
                  <Num className="font-bold text-white">{fatiaTop}%</Num> do parque.
                </p>
              </>
            )}

            {cartoes.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {cartoes.slice(0, 3).map((c) => (
                  <NextLink
                    key={c.titulo}
                    href={c.url}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-[12px] font-semibold text-white/90 hover:bg-white/20"
                  >
                    {c.titulo}
                    <Num className="font-extrabold text-white">{compacto(c.valor)}</Num>
                  </NextLink>
                ))}
              </div>
            )}
          </div>

          {/* Cartoes secundarios: dois por linha. Numero sozinho nao diz se e muito
              ou pouco, por isso cada um leva rotulo e faixa da propria categoria. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {cartoes.slice(3).map((c) => (
              <NextLink
                key={c.titulo}
                href={c.url}
                className="relative overflow-hidden rounded-lg border border-line bg-surface p-4 shadow-card transition-shadow hover:shadow-md"
              >
                <span className={cn('absolute inset-x-0 top-0 h-[3px]', faixaCartao(c.tom))} aria-hidden />
                <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">{c.titulo}</span>
                <Num className="mt-1 block text-2xl font-extrabold leading-none text-ink">{compacto(c.valor)}</Num>
              </NextLink>
            ))}

            {tickets && (
              <NextLink
                href={PATH_DASHBOARD.general.tickets}
                className="relative overflow-hidden rounded-lg border border-line bg-surface p-4 shadow-card transition-shadow hover:shadow-md"
              >
                <span
                  className={cn('absolute inset-x-0 top-0 h-[3px]', tickets.criticos ? 'bg-danger' : 'bg-info')}
                  aria-hidden
                />
                <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                  Chamados abertos
                </span>
                <div className="mt-1 flex items-baseline gap-2">
                  <Num className="text-2xl font-extrabold leading-none text-ink">{tickets.abertos}</Num>
                  {tickets.criticos > 0 && (
                    <span className="text-[12px] font-bold text-danger">
                      <Num>{tickets.criticos}</Num> com SLA vencido
                    </span>
                  )}
                </div>
              </NextLink>
            )}
          </div>
        </div>

        {/* ------------------------------------------------ movimento de hoje */}
        <div className="mb-4 rounded-lg border border-line bg-surface p-4 shadow-card">
          <div className="mb-3">
            <h2 className="text-[12.5px] font-bold">Movimento de hoje</h2>
            <p className="text-[12px] text-muted-foreground">
              Cada barra compara com o maior número do dia (<Num>{compacto(maiorDoDia)}</Num>). Alertas vêm primeiro
              quando existem.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {doDiaOrdenado.map((d) => {
              const acesa = Boolean(d.alerta) && d.valor > 0;
              return (
                <div key={d.titulo} className="relative overflow-hidden rounded-lg border border-line bg-surface-2 p-3">
                  {acesa && <span className={cn('absolute inset-x-0 top-0 h-[3px]', d.cor)} aria-hidden />}
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[11px] font-semibold">{d.titulo}</span>
                    <Num className={cn('text-[17px] font-extrabold leading-none', acesa ? d.texto : 'text-ink')}>
                      {compacto(d.valor)}
                    </Num>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-soft">
                    <div
                      className={cn('h-full rounded-full', d.cor)}
                      style={{ width: `${Math.min(100, (d.valor / maiorDoDia) * 100)}%` }}
                    />
                  </div>
                  <span className="mt-1.5 block text-[11px] text-muted-foreground">{d.descricao}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
            <span className={cn('font-bold', percentualRisco > 0 ? 'text-danger' : 'text-muted-foreground')}>
              <Num>{percentualRisco}%</Num> do parque
            </span>
            <span className="text-muted-foreground">
              teve perda/roubo ou inativação hoje — <Num>{emRisco}</Num> de{' '}
              <Num>{dados.dispositivos.toLocaleString('pt-BR')}</Num> dispositivos.
            </span>
          </div>
        </div>

        {/* ------------------------------------------------------- duas listas */}
        <div className="mb-4 grid gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <h2 className="text-[12.5px] font-bold">Aplicativos mais instalados</h2>
            <p className="mb-3 text-[12px] text-muted-foreground">
              Sem os pacotes do próprio Android — sem esse filtro a lista é só sistema.
            </p>
            {instalados.length === 0 && <p className="text-[12px] text-faint">Sem dados no período.</p>}
            {instalados.map((a) => (
              <div key={a.packageName} className="border-t border-line py-2 first:border-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-[12px] font-semibold">{a.nome}</span>
                  <Num className="flex-none text-[12px] font-bold">
                    {Number(a.totalInstalacoes).toLocaleString('pt-BR')}
                  </Num>
                </div>
                <Num className="block truncate text-[11px] text-faint">{a.packageName}</Num>
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-soft">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{
                      width: `${maiorInstalado ? (Number(a.totalInstalacoes) / maiorInstalado) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <h2 className="text-[12.5px] font-bold">Aplicativos por consumo de dados</h2>
            {/* Sem filtro, o consumo e do PARQUE INTEIRO para todo mundo (sync global
                da API) - ao lado de "N dispositivos em 1 regional", o texto antigo fazia
                parecer que os terabytes eram da regional do usuario. */}
            <p className="mb-3 text-[12px] text-muted-foreground">
              {regional
                ? 'Só desta regional · últimos 30 dias · dados móveis + Wi-Fi.'
                : 'Todo o parque, todas as regionais · últimos 30 dias · dados móveis + Wi-Fi.'}
            </p>
            {consumo.length === 0 && <p className="text-[12px] text-faint">Sem dados no período.</p>}
            {consumo.map((c) => (
              <div key={c.packageName} className="border-t border-line py-2 first:border-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-[12px] font-semibold">{c.nome}</span>
                  <Num className="flex-none text-[12px] font-bold">{bytes(c.consumoTotal)}</Num>
                </div>
                <span className="block text-[11px] text-faint">
                  móvel <Num>{bytes(c.consumoDadosMoveis)}</Num> · wi-fi <Num>{bytes(c.consumoWifi)}</Num>
                </span>
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-soft">
                  <div
                    className="h-full rounded-full bg-info"
                    style={{ width: `${maiorConsumo ? (Number(c.consumoTotal) / maiorConsumo) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ----------------------------------------------------- atividade

            SO PARA MASTER (29/09/2026, relatado: "somente master pode ver, esta
            aparecendo para qualquer usuario").

            A lista "Logins de hoje" traz NOME e ENDERECO IP de quem entrou -
            67 pessoas no dia da reclamacao. E dado pessoal de terceiros, e
            estava visivel para qualquer usuario autenticado.

            Os dois blocos vizinhos da Home ("No sistema agora" e "Historico
            geral") ja eram restritos; este era o unico da mesma natureza que
            tinha ficado aberto.

            A guarda de tela nao basta e nao esta sozinha: `geral/home` deixou de
            devolver `loginsHojeDetalhes` para quem nao e Master, e
            `geral/logins-hoje` passou a recusar. Esconder so aqui deixaria o
            dado a um `curl` de distancia. */}
        {isMaster(user) && (
        <div className="mb-4 rounded-lg border border-line bg-surface p-4 shadow-card">
          <h2 className="text-[12.5px] font-bold">Atividade do parque</h2>
          <p className="mb-3 text-[12px] text-muted-foreground">Onde estão os dispositivos e quem entrou hoje.</p>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                Regionais com mais dispositivos
              </div>
              {topRegionais.length === 0 && <p className="text-[12px] text-faint">Sem dados.</p>}
              {topRegionais.map((r, i) => (
                <div key={r.codigo ?? i} className="border-t border-line py-2 first:border-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate text-[12px]">
                      <Num className="mr-2 text-faint">{String(i + 1).padStart(2, '0')}</Num>
                      {r.nome}
                    </span>
                    <Num className="flex-none text-[12px] font-bold">
                      {Number(r.totalDispositivos).toLocaleString('pt-BR')}
                    </Num>
                  </div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-soft">
                    <div
                      className="h-full rounded-full bg-ok"
                      style={{
                        width: `${maiorRegional ? (Number(r.totalDispositivos) / maiorRegional) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Logins de hoje</span>
                <Num className="rounded bg-soft px-1.5 text-[11px] font-bold text-muted-foreground">
                  {dados.loginsHoje}
                </Num>
              </div>
              {(dados.loginsHojeDetalhes || []).length === 0 && (
                <p className="text-[12px] text-faint">Nenhum login registrado hoje.</p>
              )}
              <div className="max-h-[260px] overflow-y-auto">
                {(dados.loginsHojeDetalhes || []).map((l) => (
                  <div
                    key={l.codigo}
                    className="flex items-baseline justify-between gap-2 border-t border-line py-2 first:border-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px] font-semibold">
                        {l.usuario?.nome || 'usuário removido'}
                      </span>
                      <Num className="block truncate text-[11px] text-faint">{l.ip}</Num>
                    </span>
                    <Num className="flex-none text-[11px] text-muted-foreground">{hora(l.dataExecucao)}</Num>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* -------------------------------------------- precisa de atencao */}
        {atencao.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold text-muted-foreground">Precisa de atenção</span>
            {atencao.map((a) => (
              <NextLink
                key={a.texto}
                href={a.url}
                className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[12px] font-semibold hover:bg-soft"
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', a.ponto)} aria-hidden />
                {a.texto}
              </NextLink>
            ))}
          </div>
        )}

        {/* SO MASTER (19/08/2026, a pedido). O feed junta acao de cadastro com
            login, IP e horario de TODOS os usuarios, de todas as categorias - e
            material de auditoria, nao painel de operacao. Quem audita e o Master.

            A checagem tambem existe no backend? `historicos/paginado` filtra por
            regional, nao por perfil - entao esconder aqui NAO fecha a rota. Fica
            registrado: quem quiser a lista continua conseguindo pela API. Fechar de
            verdade e mudanca no controller de historicos, que mexe com o que as
            outras abas de historico do sistema consomem. */}
        {/* Bloco de presenca + historico geral, os dois so para Master. Lado a
            lado em tela larga: as duas respondem "o que esta acontecendo agora",
            e separadas em duas alturas diferentes a Home ficava mais longa sem
            ganhar informacao. */}
        {/* O HISTORICO GERAL SAIU DAQUI (29/09/2026, a pedido) e virou uma aba
            em Configuracoes > Logs do sistema.

            Ele lista o que cada pessoa fez, com nome e IP: e leitura de
            AUDITORIA, nao de operacao. Na Home disputava espaco com os numeros
            do parque e era a primeira coisa que alguem via ao entrar, sem ter
            perguntado nada. Ao lado das outras tres fontes de registro ele
            responde a mesma pergunta que elas - "o que aconteceu no sistema".

            `UsuariosOnline` FICOU: "quem esta no sistema agora" e operacao, nao
            auditoria - serve para saber a quem perguntar antes de mexer em algo.
            Sem o vizinho, deixa de ser coluna estreita e ocupa a largura. */}
        {isMaster(user) && <UsuariosOnline />}
      </div>
    </Page>
  );
}
